import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CircularProgress, DialogContent } from '@mui/material'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import RewardsAPI from 'api/faber/RewardsAPI'
import UsaAPI from 'api/faber/UsaAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import {
  BootstrapDialog,
  BootstrapDialogTitle
} from 'common/AbstractModal/BootstrapDialog'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'
import { GetPromocodeUsersResponse } from 'types/api/faber/reward.type'

interface PromocodeUsersDialogProps {
  promocode: string
  openDialog: boolean
  handleCloseDialog: () => void
}

interface PromocodeUser {
  index: number
  user_id: string
}

interface PromoUsers {
  userId: string
}

const PromocodeUsersDialog = ({
  promocode,
  openDialog,
  handleCloseDialog
}: PromocodeUsersDialogProps) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data, isError, isFetching, isLoading, failureCount } = useQuery({
    queryKey: ['promocodeUsers', promocode],
    queryFn: (): Promise<GetPromocodeUsersResponse> =>
      RewardsAPI.getPromocodeUsers({ promocode, faberToken }),
    retry: 3,
    retryDelay: 200 //ms
  })
  useEffect(() => {
    if (isError && !isFetching && failureCount === 4) {
      toast.error('Error fetching promocode users', {
        toastId: 'error-get-promocode-users'
      })
    }
  }, [isError, isFetching, failureCount])
  const navigate = useNavigate()

  const columns: GridColDef[] = [
    {
      field: 'index',
      headerName: '#',
      flex: 0.1,
      minWidth: 20
    },
    {
      field: 'user_id',
      headerName: 'User ID',
      flex: 1,
      renderCell: (params) => (
        <div onClick={() => handleViewUser(params.value)}>
          <NormalText isBold={false} theme='blue' isUnderlined={true}>
            {params.value}
          </NormalText>
        </div>
      )
    }
  ]

  const users: PromocodeUser[] = data
    ? (data.data as unknown as PromoUsers[]).map(
        (u: PromoUsers, index: number) => ({
          index: index + 1,
          user_id: u.userId
        })
      )
    : []

  const getUsers = async (user_id: string) => {
    if (user_id.includes('|')) {
      // Global users
      const response = await UsaAPI.getAllGlobalUsers({
        searchCriteria: 'User ID',
        searchValue: user_id,
        faberToken: faberToken
      })
      if (response.status === 200) {
        // Mapping present in FaberGlobalUserSearch.tsx
        return response.data.data.users.map(
          ({ name, family_name, given_name, user_id, user_metadata }) => ({
            name,
            family_name,
            given_name,
            user_id,
            user_metadata,
            login_method: user_id
          })
        )
      }
    } else {
      const response = await OneTimePackApi.getAllFaberUsers({
        searchCriteria: 'Customer ID',
        searchValue: user_id,
        faberToken: faberToken,
        limit: 10,
        planStatus: 'active',
        offset: 0
      })
      if (response.status === 200) {
        // Mapping present in RevampedFaberCustomerDashboard.tsx
        return response.data.body.data.map(
          ({
            _id,
            user_id,
            user_metadata,
            email,
            contactNo,
            address,
            createdAt,
            firstName,
            lastName,
            role,
            showPlanPage,
            showTutorial,
            sin,
            status,
            updatedAt,
            subscription,
            otpack
          }) => ({
            _id,
            user_id,
            user_metadata,
            contactNo,
            firstName,
            email,
            msisdn: contactNo.number,
            address,
            createdAt,
            lastName,
            role,
            showPlanPage,
            showTutorial,
            sin,
            status,
            updatedAt,
            subscription,
            otpack
          })
        )
      }
    }
    return []
  }

  const handleViewUser = async (user_id: string) => {
    const userId = user_id
    const userInfo = await getUsers(user_id)
    if (userInfo.length == 1) {
      navigate(`view-profile/${userId}`, { state: userInfo[0] })
    } else {
      toast.error('Error fetching promocode user profile', {
        toastId: 'error-get-promocode-user-profile'
      })
    }
  }

  const handleRowDoubleClick = async (params: GridRowParams) => {
    const userId = params.row.user_id
    const userInfo = await getUsers(params.row.user_id)
    if (userInfo.length == 1) {
      navigate(`view-profile/${userId}`, { state: userInfo[0] })
    } else {
      toast.error('Error fetching promocode user profile', {
        toastId: 'error-get-promocode-user-profile'
      })
    }
  }

  return (
    <BootstrapDialog
      fullWidth
      maxWidth='lg'
      open={openDialog}
      onClose={handleCloseDialog}
    >
      <BootstrapDialogTitle onClose={handleCloseDialog}>
        Users that consumed promocode {promocode}
      </BootstrapDialogTitle>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <DialogContent dividers>
          {users.length > 0 && (
            <NormalText isBold={true}>
              Double click each User ID to redirect to user profile
            </NormalText>
          )}
          <MUIDataGrid
            columns={columns}
            rows={users}
            getRowId={(row) => `${row.user_id}`}
            onRowDoubleClick={handleRowDoubleClick}
          />
        </DialogContent>
      )}
    </BootstrapDialog>
  )
}

export default PromocodeUsersDialog
